// @ts-strict-ignore
import {
  ChartPlotStyle,
  ChartType,
  InsightsBookmarkParams,
  InsightsResourceType,
  PRIMITIVE_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {ChartTypeDisplayNameKey} from 'common/report/constants';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {LiftType, LiftTypeOverrides} from 'common/report/util/lift/types';
import {getLiftTypeOverrides} from 'common/report/util/lift/overrides';
import {disableLiftTypeForChart} from 'common/report/util/lift';
import {getBehaviorShowClauses, isRetentionOnlyInsightsBookmark} from 'common/report/insights/models/utils';

export function getLiftTypeOverridesForInsightsBookmark(bookmarkParams: InsightsBookmarkParams): LiftTypeOverrides {
  const {
    displayOptions: {chartType, plotStyle},
    sections: {show: showClauses, group},
  } = bookmarkParams;

  const behaviorShowClauses = getBehaviorShowClauses(showClauses);
  const hasDistributions = behaviorShowClauses.some(
    (clause) => clause.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM,
  );
  const isProfileQuery = behaviorShowClauses.some(
    (clause) => clause.behavior.resourceType === InsightsResourceType.People,
  );

  const hasBreakdowns = getSelectedSegments(group.map((grp) => new GroupClause(grp))).length > 0;

  const multipleMetricsAllTotals =
    behaviorShowClauses.length > 1 &&
    behaviorShowClauses.every((clause) => clause.measurement.math === PRIMITIVE_MATH_TYPES.TOTAL);

  const overrides = getLiftTypeOverrides(chartType, {
    isStacked: plotStyle === ChartPlotStyle.Stacked,
  });

  if (hasBreakdowns && hasDistributions) {
    overrides[LiftType.Segment] = {
      enabled: false,
      reason: ({t}) => t(`reports.chart-types.disabled-reasons.comparison-restriction`),
    };
  } else if (multipleMetricsAllTotals) {
    // explicitly ENABLE compare to overall if >1 metric AND they are all only total count metrics
    overrides[LiftType.Overall] = {
      enabled: true,
    };
  }

  if (isProfileQuery && [ChartType.Line, ChartType.Column].includes(chartType)) {
    overrides[LiftType.Segment] = disableLiftTypeForChart(LiftType.Segment, chartType, ({t}) =>
      t(`reports.chart-types.labels.profile-chart`, {chartType: t(ChartTypeDisplayNameKey[chartType])}),
    );
    overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, chartType, ({t}) =>
      t(`reports.chart-types.labels.profile-chart`, {chartType: t(ChartTypeDisplayNameKey[chartType])}),
    );
  }

  if (isRetentionOnlyInsightsBookmark(bookmarkParams)) {
    switch (chartType) {
      case ChartType.Line:
      case ChartType.Column:
        overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, chartType, ({t}) =>
          t(`reports.chart-types.labels.retention-chart`, {chartType: t(ChartTypeDisplayNameKey[chartType])}),
        );
        break;
      case ChartType.Metric:
        overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, ChartType.Metric, ({t}) =>
          t(`reports.chart-types.labels.retention-metric`),
        );
        break;
      case ChartType.RetentionCurve:
        overrides[LiftType.Overall] = disableLiftTypeForChart(LiftType.Overall, ChartType.RetentionCurve);
        break;
    }
  }

  return overrides;
}
